import { Apple_Client_ID, Apple_Redirect_Handler_URI } from './path';

/**
 * Sign in with Apple: iOS redirect helpers.
 *
 * On iOS (all WebKit browsers and in-app webviews) Apple delivers the sign-in result via a
 * top-level redirect (form_post), not the popup `postMessage` callback the desktop flow relies on.
 * The app only handles the popup callback, so on iOS the credential is dropped and the user is
 * bounced back to the screen they started on. These helpers route iOS users through an explicit
 * redirect flow that lands on /api/apple/callback instead.
 */

// Base64url encode/decode used to round-trip small bits of context (referral code, invitation)
// through Apple's `state` parameter, since a full-page redirect discards all client-side state.
export const buildAppleState = (data: Record<string, any> = {}): string => {
  try {
    const clean: Record<string, any> = {};
    Object.entries(data).forEach(([k, v]) => {
      if (v !== undefined && v !== null && `${v}`.trim() !== '') clean[k] = v;
    });
    const json = JSON.stringify(clean);
    const b64 = typeof window !== 'undefined' ? window.btoa(json) : Buffer.from(json).toString('base64');
    return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  } catch {
    return '';
  }
};

export const parseAppleState = (state?: string): Record<string, any> => {
  if (!state) return {};
  try {
    const b64 = state.replace(/-/g, '+').replace(/_/g, '/');
    const json = typeof window !== 'undefined' ? window.atob(b64) : Buffer.from(b64, 'base64').toString('utf8');
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

export const isIOSDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iOS = /iP(hone|ad|od)/.test(ua);
  // iPadOS 13+ reports as MacIntel but exposes touch points.
  const iPadOS = navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1;
  return iOS || iPadOS;
};

const randomNonce = (): string => {
  try {
    const c: any = typeof window !== 'undefined' ? (window.crypto || (window as any).msCrypto) : null;
    if (c?.randomUUID) return c.randomUUID();
    if (c?.getRandomValues) {
      const arr = new Uint8Array(16);
      c.getRandomValues(arr);
      return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
    }
  } catch {
    /* fall through */
  }
  return `${Date.now()}${Math.floor(Math.random() * 1e9)}`;
};

/**
 * Start Sign in with Apple in redirect (form_post) mode with a correctly-encoded authorize URL.
 * We request `code id_token` so /api/apple/callback can complete login from the id_token alone
 * (via the existing /auth/appleauth-native endpoint) with no code exchange and no redirect_uri
 * coupling. react-apple-login's own redirect builder is bypassed because it does not encode params.
 */
export const startAppleRedirectLogin = (context: Record<string, any> = {}): void => {
  const params: Record<string, string> = {
    response_type: 'code id_token',
    response_mode: 'form_post',
    client_id: Apple_Client_ID,
    redirect_uri: Apple_Redirect_Handler_URI,
    scope: 'name email',
    state: buildAppleState(context),
    nonce: randomNonce(),
  };
  const qs = Object.entries(params)
    .filter(([, v]) => v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');
  window.location.href = `https://appleid.apple.com/auth/authorize?${qs}`;
};
